import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import router from '@/router';
import { useSanitizeForm } from '@/composables/useSanitizeForm';
import { sleep } from '@/utils/utils';
import { showAlert } from './utils/sweetAlert';

const instance = axios.create({
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: true,
});

const publicEndpoints = [
    '/countries',
    '/languages',
    '/states/{countryId}',
    '/cities/{stateId}',
    '/economic-activities',
    '/organization-categories',
    '/organization-types',
    '/select-plans',
    '/billing-period-types',
    '/statuses',
];

instance.interceptors.request.use(

    (config) => {

        const authStore = useAuthStore();
        const { sanitizeForm } = useSanitizeForm();

        let token = authStore.accessToken;
        const subdomain = authStore.subdomain;
        const scopeId = authStore.scope?.id;

        if (publicEndpoints.some(endpoint => config.url.includes(endpoint))) {

            token = authStore.clientAccessToken;

        }

        if (token && !config.headers['Authorization']) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        if (authStore.baseUrl) {
            config.baseURL = authStore.baseUrl;
        }

        if (subdomain && !config.headers['X-Subdomain']) {
            config.headers['X-Subdomain'] = subdomain;
        }

        if (scopeId) {
            config.headers['X-ScopeId'] = scopeId;
        }

        const lang = typeof authStore.language === 'string' ? authStore.language : (authStore.language?.code ?? 'es');
        config.headers['X-Language'] = lang;

        if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
            config.data = sanitizeForm(config.data);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

const cookieMode = process.env.VUE_APP_COOKIE_MODE === 'true';

let sessionExpiredHandled = false;
let refreshPromise = null;

function refreshSession() {

    if (!refreshPromise) {

        refreshPromise = instance
            .post('auth/refresh', {}, { skipAuthRedirect: true, _isRefresh: true })
            .finally(() => { refreshPromise = null; });

    }

    return refreshPromise;
}

instance.interceptors.response.use(
    (response) => response,
    async (error) => {

        const originalRequest = error.config;

        if (error.response?.status === 403) {

            router.push({ name: 'InsufficientPermissionsPage' });
            return new Promise(() => {});
        }

        if (error.response?.status === 401) {

            if (
                cookieMode &&
                originalRequest &&
                !originalRequest._retry &&
                !originalRequest._isRefresh &&
                !originalRequest.skipAuthRedirect
            ) {

                originalRequest._retry = true;

                try {

                    await refreshSession();
                    return instance(originalRequest);

                } catch (refreshError) {
                    // refresh failed; fall through to the session-expired flow below
                }
            }

            if (originalRequest?.skipAuthRedirect) {
                return Promise.reject(error);
            }

            if (sessionExpiredHandled) {
                return new Promise(() => {});
            }

            sessionExpiredHandled = true;

            const authStore = useAuthStore();
            authStore.clearAuthData();

            setTimeout(() => {

                showAlert({
                    icon: 'info',
                    title: 'Sesión expirada',
                    message: 'Debes iniciar sesión nuevamente',

                }).then(() => {

                    const currentRoute = router.currentRoute.value;

                    const isMSPIRoute =
                        currentRoute.name === 'mspi-evaluation' ||
                        currentRoute.path.includes('/mspi-evaluation') ||
                        currentRoute.name === 'mspi' ||
                        currentRoute.path.includes('/mspi');

                    if (isMSPIRoute) {

                        window.location.href = `${process.env.VUE_APP_FRONTEND_URL}/mspi`;

                    } else {

                        window.location.href = process.env.VUE_APP_FRONTEND_URL;

                    }

                });
            }, 1000);

            return new Promise(() => {});
        }


        return Promise.reject(error);
    }
);

export const GET = async (url, config = {}) => {

    await sleep(0);
    return instance.get(url, config);
};

export const POST = (url, data = {}, config = {}) => instance.post(url, data, config);
export const PUT = (url, data = {}, config = {}) => instance.put(url, data, config);
export const PATCH = (url, data = {}, config = {}) => instance.patch(url, data, config);
export const DELETE = (url, config = {}) => instance.delete(url, config);

export default instance;
